import {
  ClientAuthToken,
  ClientAuthTokenResponse,
  ClientRefreshTokenRequest,
  DeepReadonly,
  synchronized,
} from '..';
import {
  RequestConfig,
  HTTPResponse,
  HTTPClient,
  isHTTPError,
  HTTPRequestFactory,
  HTTPRequestCompletionCallback,
  HTTPErrorHandler,
  isHTTPErrorResponse,
  HTTPMethod,
} from './types';

interface HevyAPIClientConfig<UserContext> {
  /**
   * How long before predicted token expiry to request a new token. We request
   * new tokens a little bit before they expire, so that we don't have to wait
   * for the backend to send us a 401 first. We do it pre-emptively so that we
   * wouldn't need to wait for requests to fail first, and then retry them.
   *
   * Optional.
   *
   * @default {1 minute}
   */
  readonly tokenExpirySafetyThresholdMs?: number;
  /**
   * How long we expect the access token to be valid for. Used to override the
   * expiry time sent by the backend in case the client clock is in the future
   * and the client thinks that the token will expire sooner than it actually
   * will. Even if this assumption ever stops being correct, the client will be
   * able to self-correct by receiving an `AccessTokenExpired` 401 response.
   *
   * Optional.
   *
   * @default {15 minutes}
   */
  readonly accessTokenMinimumValidAgeMs?: number;
  /**
   * Minimum time between successive calls to the `auth/refresh_token` endpoint
   * in case it doesn't return an error. Also used to deal with potential clock
   * issues on the client side, or some unforeseen issue on the backend, as the
   * last line of defence. No matter what may happen, we absolutely never ever
   * want to end up accidentally spamming this endpoint because it could have a
   * cascading clusterfuck effect on all the other requests.
   *
   * Optional.
   *
   * @default {20 seconds}
   */
  readonly tokenRefreshThrottleMs?: number;
  /**
   * The API endpoint used to refresh the auth tokens using a refresh token.
   * The URL is relative to the backend base URL.
   *
   * Optional.
   *
   * @default {POST /auth/refresh_token}
   */
  readonly refreshAuthTokenApiEndpoint?: {
    readonly method: 'post';
    readonly url: string;
  };
  /**
   * Callback to use when receiving a new auth token. Used to send it to the
   * consumer of the class to save the new token to its local storage.
   *
   * Required.
   */
  onNewAuthToken(newAuthToken: ClientAuthToken, userContext: UserContext): void;
  /**
   * Defines an arbitrary object to be passed back to the `onNewAuthToken`. The
   * value is computed at the time when the request is made. Useful for passing
   * back state that may have gotten changed during the time while the request
   * was being processed, such as holding onto a userId across a logout action.
   *
   * Optional.
   */
  getUserContext?(): UserContext;
  /**
   * Callback to use when receiving an HTTP error response from the backend, to
   * determine whether it is a response indicating that the token has expired.
   *
   * Optional.
   *
   * @default ...
   */
  isAccessTokenExpiredResponse?(response: HTTPResponse<unknown>): boolean;
  /**
   * Callback to use when receiving an HTTP error response from the backend, to
   * determine whether it is a response indicating that the token is invalid.
   *
   * Optional.
   *
   * @default ...
   */
  isAccessTokenInvalidResponse?(response: HTTPResponse<unknown>): boolean;
}

export class HevyAPIClient<UserContext = never> {
  private static readonly DEFAULT_TOKEN_EXPIRY_SAFETY_THRESHOLD_MS = 60_000 as const;
  private static readonly DEFAULT_ACCESS_TOKEN_MINIMUM_VALID_AGE_MS = 900_000 as const;
  private static readonly DEFAULT_TOKEN_REFRESH_THROTTLE_MS = 20_000 as const;
  private static readonly DEFAULT_REFRESH_AUTH_TOKEN_API_ENDPOINT = {
    method: 'post',
    url: 'auth/refresh_token',
  } as const;

  private readonly _config: Required<HevyAPIClientConfig<UserContext>>;
  private _requestCompletionCallbacks: HTTPRequestCompletionCallback[] = [];
  private _errorHandlers: HTTPErrorHandler<{
    willRetry: boolean;
    isTokenRefreshedAfterRequest: boolean;
    isPreviousSession: boolean;
  }>[] = [];
  private _authTokenFactory:
    | { type: 'value'; value: ClientAuthToken | null }
    | { type: 'getter'; get: () => ClientAuthToken | null } = {
    type: 'value',
    value: null,
  };
  private _legacyAuthToken: string | null = null;
  private _httpClient: HTTPClient;
  private _lastTokenRefresh: Date | undefined;
  private _lastSessionDelete: Date | undefined;

  constructor(httpClient: HTTPClient, config: HevyAPIClientConfig<UserContext>) {
    this._httpClient = httpClient;

    this._config = {
      tokenExpirySafetyThresholdMs: HevyAPIClient.DEFAULT_TOKEN_EXPIRY_SAFETY_THRESHOLD_MS,
      accessTokenMinimumValidAgeMs: HevyAPIClient.DEFAULT_ACCESS_TOKEN_MINIMUM_VALID_AGE_MS,
      tokenRefreshThrottleMs: HevyAPIClient.DEFAULT_TOKEN_REFRESH_THROTTLE_MS,
      refreshAuthTokenApiEndpoint: HevyAPIClient.DEFAULT_REFRESH_AUTH_TOKEN_API_ENDPOINT,
      isAccessTokenExpiredResponse: (response: HTTPResponse<unknown>) =>
        response.status === 401 &&
        isHTTPErrorResponse(response) &&
        response.data.error === 'AccessTokenExpired',
      isAccessTokenInvalidResponse: (response: HTTPResponse<unknown>) =>
        response.status === 401 &&
        isHTTPErrorResponse(response) &&
        response.data.error === 'InvalidAccessToken',
      getUserContext: () => undefined!,
      ...config,
    };

    const classProperties = Object.getOwnPropertyNames(Object.getPrototypeOf(this)).filter(
      (m): m is string & keyof this => m in this && typeof m === 'string' && m !== 'constructor',
    );

    classProperties.forEach(m => {
      if (typeof this[m] === 'function') this[m] = this[m].bind(this);
    });
  }

  private get _authToken(): ClientAuthToken | null {
    switch (this._authTokenFactory.type) {
      case 'value':
        return this._authTokenFactory.value;
      case 'getter':
        return this._authTokenFactory.get();
    }
  }

  private get _authHeaders() {
    return {
      ...(this._authToken ? { authorization: `Bearer ${this._authToken.access_token}` } : {}),
      ...(this._legacyAuthToken ? { 'auth-token': this._legacyAuthToken } : {}),
      'x-client-time': String(new Date().valueOf() / 1_000),
    };
  }

  private _addHeaders = (headers: Record<string, string>, config?: RequestConfig) => ({
    ...config,
    headers: { ...config?.headers, ...headers },
  });

  @synchronized(true, 'refreshAuthToken')
  private async refreshExpiredAuthToken(userContext: unknown) {
    const { _authToken } = this;

    if (!_authToken) return;

    if (
      new Date().valueOf() <=
      new Date(_authToken.expires_at).valueOf() - this._config.tokenExpirySafetyThresholdMs
    ) {
      // Token is still valid, at least according to the client. Don't refresh.
      return;
    }

    await this._refreshAuthToken(_authToken, userContext);
  }

  @synchronized(true, 'refreshAuthToken')
  private async forceRefreshAuthToken(userContext: unknown) {
    const { _authToken } = this;

    if (!_authToken) return;

    await this._refreshAuthToken(_authToken, userContext);
  }

  @synchronized(true, 'refreshAuthToken')
  private async waitForTokenRefresh() {
    // This may look like it does nothing, but that is absolutely not the case.
    // Do not remove this function!
  }

  private get _isTokenRecentlyRefreshed(): boolean {
    const { _lastTokenRefresh } = this;

    return (
      _lastTokenRefresh !== undefined &&
      _lastTokenRefresh.valueOf() > new Date().valueOf() - this._config.tokenRefreshThrottleMs
    );
  }

  private async _refreshAuthToken(authToken: ClientAuthToken, userContext: unknown) {
    if (this._isTokenRecentlyRefreshed) {
      // We have called this function recently. Do not ever spam this endpoint.
      // Also, if any request needs to be retried due to issues with the client
      // clock being out of sync with the backend, do that with the last token
      // that was obtained after a 401 with the `AccessTokenExpired` response.
      return;
    }

    this._lastTokenRefresh = new Date();

    const { access_token, refresh_token } = authToken;

    const { method, url } = this.refreshAuthTokenApiEndpoint;
    const headers = {
      authorization: `Bearer ${access_token}`,
      'x-client-time': String(new Date().valueOf() / 1_000),
    };

    // This will throw and bail out if the request fails.
    const response = await this._handleResponse(
      {
        method,
        url,
        headers,
        try: () =>
          this._httpClient[method]<ClientAuthTokenResponse, ClientRefreshTokenRequest>(
            url,
            { refresh_token },
            { headers },
          ),
      },
      userContext,
      false,
    );

    const newToken = {
      access_token: response.data.access_token,
      refresh_token: response.data.refresh_token,
      expires_at: new Date(response.data.expires_at),
    };

    /**
     * Do not attempt to auto-refresh the token before this time - by default,
     * {@link HevyAPIClient.DEFAULT_ACCESS_TOKEN_MINIMUM_VALID_AGE_MS 15 mins}
     * after the current time. Normally the backend will generate a token that
     * lives for at least 15 minutes, but since it sends the expiry timestamp
     * rather than a max age in relative time units, even if the client's clock
     * is severely out of sync, we don't want to think that the token has
     * expired when in reality it hasn't.
     *
     * If we ever change the access tokens to really live for less than 15 min,
     * this assertion will no longer hold, and any tokens that are expired
     * despite the client not realising it will be handled by the logic that
     * handles 401s with the `AccessTokenExpired` error response.
     */
    const earliestAutoRefreshUnixMs =
      new Date().valueOf() + this._config.accessTokenMinimumValidAgeMs;

    if (newToken.expires_at.valueOf() < earliestAutoRefreshUnixMs) {
      newToken.expires_at = new Date(earliestAutoRefreshUnixMs);
    }

    if (this._authTokenFactory.type === 'value') {
      this._authTokenFactory.value = newToken;
    }

    (this._config.onNewAuthToken as (newAuthToken: ClientAuthToken, userContext: unknown) => void)(
      newToken,
      userContext,
    );
  }

  private async _handleResponse<T>(
    request: HTTPRequestFactory<T>,
    userContext: unknown,
    retryOnFailure: boolean = !this._isTokenRecentlyRefreshed,
  ): Promise<HTTPResponse<T>> {
    const requestTime = new Date();

    try {
      const response = await request.try();

      if (response.status >= 400) {
        throw Object.assign(new Error(`Request failed with status code ${response.status}`), {
          response,
        });
      }

      this._requestCompletionCallbacks.forEach(cb =>
        cb({ isSuccess: true, value: response }, request),
      );

      return response;
    } catch (e: unknown) {
      this._requestCompletionCallbacks.forEach(cb => cb({ isSuccess: false, error: e }, request));

      if (!isHTTPError(e)) throw e;

      const { response } = e;

      /**
       * We've had a {@link _refreshAuthToken} called _after_ this request was
       * made. If that request reached the server before this one did, this one
       * will have failed with a 401 because it's using the old token.
       *
       * In this (relatively unlikely) scenario, we will want to retry the
       * request, and also we will not want to log the user out.
       */
      const isTokenRefreshedAfterRequest =
        this._lastTokenRefresh !== undefined && requestTime < this._lastTokenRefresh;

      /**
       * Similar to the above, but this will be `true` if the user has logged
       * out after the request was made, i.e. the session that the request was
       * made within is is (probably) no longer valid. In this case, we don't
       * want to retry the request, but we also don't want to log the user out,
       * in case that they've logged in to another session in the meantime.
       */
      const isPreviousSession =
        this._lastSessionDelete !== undefined && requestTime < this._lastSessionDelete;

      this._errorHandlers.forEach(cb =>
        cb(response, request, {
          willRetry: retryOnFailure,
          isTokenRefreshedAfterRequest,
          isPreviousSession,
        }),
      );

      if (retryOnFailure && this.isAccessTokenExpiredResponse(response)) {
        await this.forceRefreshAuthToken(userContext);
        return await this._handleResponse(request, userContext, false);
      } else if (
        retryOnFailure &&
        this.isAccessTokenInvalidResponse(response) &&
        isTokenRefreshedAfterRequest &&
        !isPreviousSession
      ) {
        await this.waitForTokenRefresh();
        return await this._handleResponse(request, userContext, false);
      } else {
        throw e;
      }
    }
  }

  public isAccessTokenExpiredResponse(response: HTTPResponse<unknown>) {
    return this._config.isAccessTokenExpiredResponse(response);
  }

  public isAccessTokenInvalidResponse(response: HTTPResponse<unknown>) {
    return this._config.isAccessTokenInvalidResponse(response);
  }

  public get refreshAuthTokenApiEndpoint() {
    return { ...this._config.refreshAuthTokenApiEndpoint } as const;
  }

  public async setAuthToken(newAuthToken: {
    authToken: DeepReadonly<ClientAuthToken> | null;
    legacyAuthToken: string | null;
  }): Promise<void>;

  public async setAuthToken(newAuthToken: {
    getAuthToken: () => ClientAuthToken | null;
    legacyAuthToken: string | null;
  }): Promise<void>;

  public async setAuthToken(newAuthToken: {
    authToken?: DeepReadonly<ClientAuthToken> | null;
    getAuthToken?: () => ClientAuthToken | null;
    legacyAuthToken: string | null;
  }) {
    await this.waitForTokenRefresh();

    const { authToken, getAuthToken, legacyAuthToken } = newAuthToken;

    if (getAuthToken !== undefined && authToken === undefined) {
      this._authTokenFactory = { type: 'getter', get: getAuthToken };
    } else if (authToken !== undefined && getAuthToken === undefined) {
      this._authTokenFactory = { type: 'value', value: authToken };
    } else {
      throw new Error(
        "Invalid arguments: exactly one of `authToken' or `getAuthToken' is required",
      );
    }

    this._legacyAuthToken = legacyAuthToken;
    this._lastTokenRefresh = undefined;
  }

  public async clearAuthToken() {
    await this.waitForTokenRefresh();

    this._authTokenFactory = { type: 'value', value: null };
    this._legacyAuthToken = null;
    this._lastTokenRefresh = undefined;
  }

  public get isAuthenticated(): boolean {
    if (this._authTokenFactory.type === 'value' && this._authTokenFactory.value === null) {
      return false;
    } else {
      return true;
    }
  }

  public markSessionDeleted(): void {
    this._lastSessionDelete = new Date();
  }

  /**
   * Adds a callback to be executed whenever a request has finished processing.
   * This means either that the request has received a response, or that there
   * was an error. In the case of an error, it may be either an HTTP error from
   * the server, or some other type of error, such as a network error.
   *
   * This is a lower level API than {@link attachErrorHandler} - prefer using
   * that one instead of this one if it's enough to suit your needs.
   */
  public attachRequestCompletionCallback(onResult: HTTPRequestCompletionCallback) {
    this._requestCompletionCallbacks.push(onResult);
  }

  public removeRequestCompletionCallbacks() {
    this._requestCompletionCallbacks.length = 0;
  }

  /**
   * Adds a callback to be executed on receiving an HTTP error from the server.
   * This callback will not be executed for any other type of error, such as a
   * network error. For that and more, use {@link attachRequestCompletionCallback}.
   */
  public attachErrorHandler(
    onError: HTTPErrorHandler<{
      willRetry: boolean;
      isTokenRefreshedAfterRequest: boolean;
      isPreviousSession: boolean;
    }>,
  ) {
    this._errorHandlers.push(onError);
  }

  public removeErrorHandlers() {
    this._errorHandlers.length = 0;
  }

  public async get<T = never>(url: string, config?: RequestConfig): Promise<HTTPResponse<T>> {
    const userContext = this._config.getUserContext();

    await this.refreshExpiredAuthToken(userContext);

    const method: HTTPMethod = 'get';

    return this._handleResponse(
      {
        method,
        url,
        headers: this._authHeaders,
        try: () => this._httpClient[method](url, this._addHeaders(this._authHeaders, config)),
      },
      userContext,
    );
  }

  public async delete<T = never>(url: string, config?: RequestConfig): Promise<HTTPResponse<T>> {
    const userContext = this._config.getUserContext();

    await this.refreshExpiredAuthToken(userContext);

    const method: HTTPMethod = 'delete';

    return this._handleResponse(
      {
        method,
        url,
        headers: this._authHeaders,
        try: () => this._httpClient[method](url, this._addHeaders(this._authHeaders, config)),
      },
      userContext,
    );
  }

  public async head<T = never>(url: string, config?: RequestConfig): Promise<HTTPResponse<T>> {
    const userContext = this._config.getUserContext();

    await this.refreshExpiredAuthToken(userContext);

    const method: HTTPMethod = 'head';

    return this._handleResponse(
      {
        method,
        url,
        headers: this._authHeaders,
        try: () => this._httpClient[method](url, this._addHeaders(this._authHeaders, config)),
      },
      userContext,
    );
  }

  public async options<T = never>(url: string, config?: RequestConfig): Promise<HTTPResponse<T>> {
    const userContext = this._config.getUserContext();

    await this.refreshExpiredAuthToken(userContext);

    const method: HTTPMethod = 'options';

    return this._handleResponse(
      {
        method,
        url,
        headers: this._authHeaders,
        try: () => this._httpClient[method](url, this._addHeaders(this._authHeaders, config)),
      },
      userContext,
    );
  }

  public async post<T = never, R = unknown>(
    url: string,
    data?: R,
    config?: RequestConfig,
  ): Promise<HTTPResponse<T>> {
    const userContext = this._config.getUserContext();

    await this.refreshExpiredAuthToken(userContext);

    const method: HTTPMethod = 'post';

    return this._handleResponse(
      {
        method,
        url,
        headers: this._authHeaders,
        try: () => this._httpClient[method](url, data, this._addHeaders(this._authHeaders, config)),
      },
      userContext,
    );
  }

  public async put<T = never, R = unknown>(
    url: string,
    data?: R,
    config?: RequestConfig,
  ): Promise<HTTPResponse<T>> {
    const userContext = this._config.getUserContext();

    await this.refreshExpiredAuthToken(userContext);

    const method: HTTPMethod = 'put';

    return this._handleResponse(
      {
        method,
        url,
        headers: this._authHeaders,
        try: () => this._httpClient[method](url, data, this._addHeaders(this._authHeaders, config)),
      },
      userContext,
    );
  }

  public async patch<T = never, R = unknown>(
    url: string,
    data?: R,
    config?: RequestConfig,
  ): Promise<HTTPResponse<T>> {
    const userContext = this._config.getUserContext();

    await this.refreshExpiredAuthToken(userContext);

    const method: HTTPMethod = 'patch';

    return this._handleResponse(
      {
        method,
        url,
        headers: this._authHeaders,
        try: () => this._httpClient[method](url, data, this._addHeaders(this._authHeaders, config)),
      },
      userContext,
    );
  }
}
